import { useState, useEffect, useCallback, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, collection, query, orderBy, limit, onSnapshot, doc } from "firebase/firestore";
import { toast } from "react-toastify";

// Global singleton to prevent duplicate API calls across all hook instances
let globalInitializationPromise = null;
let globalHasInitialized = false;
let globalData = null;

// Reset global state when page reloads
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    globalInitializationPromise = null;
    globalHasInitialized = false;
    globalData = null;
  });
}

export const useAdminData = (isEnabled = true, authContextLoaded = true) => {
  
  const [status, setStatus] = useState(null);
  const [recentExecutions, setRecentExecutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [isSystemInitialized, setIsSystemInitialized] = useState(null);
  const [isLoading, setIsLoading] = useState(isEnabled);
  const [error, setError] = useState(null);

  // Note: Using global singleton pattern instead of per-hook instance guards
  
  // Create stable references to avoid unnecessary re-renders
  const functionsRef = useRef(null);
  const dbRef = useRef(null);
  
  if (!functionsRef.current) {
    functionsRef.current = getFunctions();
  }
  if (!dbRef.current) {
    dbRef.current = getFirestore();
  }
  
  const functions = functionsRef.current;
  const db = dbRef.current;

  // Fetch initial admin data (only called once globally)
  const fetchInitialAdminData = useCallback(async () => {
    // Always check initialization status, even for non-superAdmin users
    // This is needed for the initial setup flow when no superAdmins exist
    if (!isEnabled) {
      try {
        setIsLoading(true);
        setError(null);
        
        const getUsersWithClaims = httpsCallable(functions, "admin-getUsersWithClaims");
        
        const usersResult = await getUsersWithClaims({}, { timeout: 30000 });
        setUsers(usersResult.data.users || []);
        setIsSystemInitialized(!usersResult.data.needsInitialization);
      } catch (err) {
        console.error('[useAdminData] Error checking initialization status:', err);
        setError(err.message || "Failed to check system status");
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // If already initialized globally, use cached data
    if (globalHasInitialized && globalData) {
      setStats(globalData.stats);
      setUsers(globalData.users);
      setIsSystemInitialized(globalData.isSystemInitialized);
      setIsLoading(false);
      return;
    }
    
    // If there's already a promise in progress, wait for it
    if (globalInitializationPromise) {
      try {
        const result = await globalInitializationPromise;
        setStats(result.stats);
        setUsers(result.users);
        setIsSystemInitialized(result.isSystemInitialized);
        setIsLoading(false);
        return;
      } catch (err) {
        setError(err.message || "Failed to fetch admin data");
        setIsLoading(false);
        return;
      }
    }
    
    // Create the initialization promise (this should only happen once per page load)
    globalInitializationPromise = (async () => {
      try {
        // Create the callable functions with timeout
        const getDataParserStatus = httpsCallable(functions, "admin-getDataParserStatus");
        const getUsersWithClaims = httpsCallable(functions, "admin-getUsersWithClaims");
        
        // Note: Making calls sequentially due to Firebase emulator duplicating parallel requests
        const statusResult = await getDataParserStatus({}, { timeout: 30000 });
        const usersResult = await getUsersWithClaims({}, { timeout: 30000 });
        
        const result = {
          stats: statusResult.data.stats,
          users: usersResult.data.users || [],
          isSystemInitialized: !usersResult.data.needsInitialization
        };
        
        // Cache the result globally
        globalData = result;
        globalHasInitialized = true;
        
        return result;
        
      } catch (err) {
        console.error("Error fetching admin data:", err);
        globalInitializationPromise = null; // Reset promise on error
        throw err;
      }
    })();
    
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await globalInitializationPromise;
      setStats(result.stats);
      setUsers(result.users);
      setIsSystemInitialized(result.isSystemInitialized);
    } catch (err) {
      console.error('[useAdminData] Error fetching admin data:', err);
      setError(err.message || "Failed to fetch admin data");
      toast.error("Failed to load admin data");
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled]);

  // Set up real-time listeners
  useEffect(() => {
    if (!isEnabled) return;
    
    const unsubscribers = [];

    // Listen to execution history
    const executionsQuery = query(
      collection(db, "dataParser", "history", "executions"),
      orderBy("startTime", "desc"),
      limit(10)
    );

    const executionsUnsubscribe = onSnapshot(
      executionsQuery,
      (snapshot) => {
        const executions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Convert Firestore timestamps to JavaScript Date objects
          startTime: doc.data().startTime?.toDate(),
          endTime: doc.data().endTime?.toDate()
        }));
        setRecentExecutions(executions);

        // Determine parser status from executions
        const runningExecution = executions.find(exec => exec.status === "running");
        setStatus({
          isRunning: !!runningExecution,
          currentExecution: runningExecution || null,
          lastUpdated: new Date().toISOString()
        });
      },
      (err) => {
        console.error("Error listening to execution history:", err);
        setError("Failed to listen to execution updates");
      }
    );

    unsubscribers.push(executionsUnsubscribe);

    // Listen to statistics updates in real-time
    const statsUnsubscribe = onSnapshot(
      doc(db, "dataParser", "config", "stats", "main"),
      (snapshot) => {
        if (snapshot.exists()) {
          console.log('[useAdminData] Stats updated via real-time listener:', snapshot.data());
          setStats(snapshot.data());
        } else {
          console.log('[useAdminData] Stats document does not exist');
        }
      },
      (err) => {
        console.error("Error listening to stats updates:", err);
        // Don't set error for stats listener failure - stats can still be fetched initially
      }
    );

    unsubscribers.push(statsUnsubscribe);

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [isEnabled]);

  // Trigger manual data parser run
  const triggerManualRun = useCallback(async (options = {}) => {
    if (!isEnabled) return;
    
    try {
      setIsLoading(true);
      
      const manualDataParserTrigger = httpsCallable(functions, "admin-manualDataParserTrigger");
      const result = await manualDataParserTrigger(options, { timeout: 900000 }); // 15 minutes
      
      toast.success("Data parser triggered successfully!");
      
      return result.data;
    } catch (err) {
      console.error("Error triggering manual run:", err);
      const errorMessage = err.message || "Failed to trigger data parser";
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled]);


  // Initial data fetch - wait for auth context to load
  useEffect(() => {
    if (authContextLoaded) {
      fetchInitialAdminData();
    }
  }, [fetchInitialAdminData, isEnabled, authContextLoaded]);

  return {
    status,
    recentExecutions,
    stats,
    users,
    isSystemInitialized,
    isLoading,
    error,
    triggerManualRun
  };
};